/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useSelectedProject } from '../hooks/useSelectedProject';
import { objectStorageService } from '../services/objectStorageService';
import type { StorageBucket, StorageUsage } from '../types/objectStorage';
import CreateBucketModal from '../components/drive/CreateBucketModal';
import BucketCard from '../components/drive/BucketCard';
import BucketModal from '../components/drive/BucketModal';
import { formatBytes } from '../utils/formatBytes';
import {
  Page,
  PageHeader,
  Card,
  Button,
  StatTile,
  EmptyState,
  Skeleton,
  formatINR,
} from '../components/console/ui';
import { DriveIc, PlusIc } from '../components/console/icons';

const ALL = 'all';

type ScopedBucket = StorageBucket & { project_id: string };

type CreatePayload = {
  name: string;
  region: string;
  versioning: boolean;
  is_public: boolean;
  plan_type: 'starter' | 'committed';
  min_commitment_tb?: number;
};

export default function PodstackDrive() {
  const [searchParams] = useSearchParams();
  const projectFromUrl = searchParams.get('project');
  const { selectedProjectId: globalProjectId, projects, autoSelectFirstProject } = useSelectedProject();
  useEffect(() => {
    if (!projectFromUrl) autoSelectFirstProject();
  }, [projectFromUrl, autoSelectFirstProject]);
  const queryClient = useQueryClient();

  // Scope defaults to the URL param, else the top-bar project. "All projects"
  // fans out per project because the object-storage API is project-scoped.
  const [scope, setScope] = useState<string>(projectFromUrl || globalProjectId || '');
  useEffect(() => {
    setScope(projectFromUrl || globalProjectId || '');
  }, [projectFromUrl, globalProjectId]);

  const projectIds: string[] = useMemo(
    () => (scope === ALL ? projects.map((p: any) => p.id as string) : scope ? [scope] : []),
    [scope, projects],
  );
  const projectName = (id: string) => projects.find((p: any) => p.id === id)?.name || '-';

  const [createFor, setCreateFor] = useState<{ projectId: string; name?: string; isPublic?: boolean } | null>(null);
  const [openBucket, setOpenBucket] = useState<{ id: string; tab: 'files' | 'access' | 'settings' } | null>(null);
  const [deletingBucketId, setDeletingBucketId] = useState<string | null>(null);

  // Inline "Create your first bucket" form
  const [quickName, setQuickName] = useState('');
  const [quickProject, setQuickProject] = useState('');
  const [quickPublic, setQuickPublic] = useState(false);

  const usageQueries = useQueries({
    queries: projectIds.map((pid) => ({
      queryKey: ['storage-usage', pid],
      queryFn: () => objectStorageService.getUsage(pid),
      enabled: !!pid,
    })),
  });

  const bucketQueries = useQueries({
    queries: projectIds.map((pid) => ({
      queryKey: ['buckets', pid],
      queryFn: () => objectStorageService.listBuckets(pid),
      enabled: !!pid,
    })),
  });

  const bucketsKey = bucketQueries.map((q) => q.dataUpdatedAt).join(',');
  const buckets: ScopedBucket[] = useMemo(
    () => bucketQueries.flatMap((q, i) => (q.data || []).map((b) => ({ ...b, project_id: projectIds[i] }))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bucketsKey, projectIds],
  );
  const isLoading = bucketQueries.some((q) => q.isLoading);

  const usageLoaded = usageQueries.length > 0 && usageQueries.every((q) => q.data);
  const usage: StorageUsage | null = usageLoaded
    ? usageQueries.reduce(
        (acc, q) => ({
          storage_bytes: acc.storage_bytes + (q.data?.storage_bytes || 0),
          object_count: acc.object_count + (q.data?.object_count || 0),
          bucket_count: acc.bucket_count + (q.data?.bucket_count || 0),
        }),
        { storage_bytes: 0, object_count: 0, bucket_count: 0 },
      )
    : null;
  const monthlyPlans = buckets.reduce((sum, b) => sum + (b.plan_monthly_price_inr || 0), 0);

  const invalidate = (pid: string) => {
    queryClient.invalidateQueries({ queryKey: ['buckets', pid] });
    queryClient.invalidateQueries({ queryKey: ['storage-usage', pid] });
  };

  const createMutation = useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: CreatePayload }) =>
      objectStorageService.createBucket(projectId, data),
    onSuccess: (bucket, vars) => {
      toast.success(`Bucket "${bucket.name}" created`);
      setCreateFor(null);
      setQuickName('');
      invalidate(vars.projectId);
    },
    onError: (err: any) => {
      const status = err?.response?.status;
      const msg = err?.response?.data?.error ?? 'Failed to create bucket';
      if (status === 402) {
        toast(
          (t) => (
            <div className="space-y-2">
              <p className="text-sm font-medium text-white">Insufficient wallet balance</p>
              <p className="text-xs text-gray-300">{msg}</p>
              <div className="flex gap-2">
                <a
                  href="/portal/wallet"
                  onClick={() => toast.dismiss(t.id)}
                  className="text-xs px-3 py-1 bg-primary-500 hover:bg-primary-600 text-white rounded"
                >
                  Top up wallet
                </a>
                <button
                  onClick={() => toast.dismiss(t.id)}
                  className="text-xs px-3 py-1 border border-gray-600 text-gray-300 rounded"
                >
                  Dismiss
                </button>
              </div>
            </div>
          ),
          { duration: 15000, style: { background: '#1f2937', color: '#fff', maxWidth: 480 } },
        );
        return;
      }
      toast.error(msg);
    },
  });

  const deletingBucket = buckets.find((b) => b.id === deletingBucketId);

  const deleteMutation = useMutation({
    mutationFn: (b: ScopedBucket) => objectStorageService.deleteBucket(b.project_id, b.id),
    onSuccess: (_r, b) => {
      toast.success('Bucket deleted');
      setDeletingBucketId(null);
      invalidate(b.project_id);
    },
    onError: () => toast.error('Failed to delete bucket'),
  });

  const defaultCreateProject = scope && scope !== ALL ? scope : globalProjectId || projects[0]?.id || '';
  const openCreate = () => {
    if (!defaultCreateProject) {
      toast.error('Create a project first');
      return;
    }
    setCreateFor({ projectId: defaultCreateProject });
  };

  const firstBucket = buckets[0];
  const s3Snippet = `aws s3 ls s3://${firstBucket?.bucket_key || firstBucket?.name || quickName || 'my-bucket'} \\
  --endpoint-url $PODSTACK_S3_ENDPOINT`;

  const scopeText =
    scope === ALL ? 'All projects shown.' : scope ? `Showing ${projectName(scope)}.` : '';

  return (
    <Page>
      <PageHeader
        title="Object storage"
        subtitle={`S3-compatible buckets for datasets, checkpoints and outputs. ${scopeText}`}
        actions={
          <>
            {!projectFromUrl && (
              <label className="flex items-center gap-2 text-sm text-ps-muted">
                <span className="sr-only">Project</span>
                <select
                  value={scope}
                  onChange={(e) => setScope(e.target.value)}
                  className="ps-input w-auto min-w-[170px]"
                >
                  {!scope && <option value="">Select a project…</option>}
                  <option value={ALL}>All projects</option>
                  {projects.map((p: any) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <Button onClick={openCreate} disabled={!defaultCreateProject}>
              <PlusIc className="w-4 h-4" />
              New bucket
            </Button>
          </>
        }
      />

      {projectIds.length === 0 ? (
        <EmptyState
          icon={<DriveIc />}
          title="Select a project"
          body="Choose a project in the top bar to view or create buckets."
        />
      ) : (
        <>
          <div className="grid gap-3.5 grid-cols-[repeat(auto-fit,minmax(min(100%,200px),1fr))]">
            {usage ? (
              <>
                <StatTile label="Storage used" value={formatBytes(usage.storage_bytes)} />
                <StatTile label="Objects" value={usage.object_count.toLocaleString()} />
                <StatTile label="Buckets" value={usage.bucket_count.toString()} />
                <StatTile label="Monthly plans" value={formatINR(monthlyPlans)} sub="Sum of bucket plan prices" />
              </>
            ) : (
              [0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-[92px]" />)
            )}
          </div>

          {isLoading ? (
            <div className="grid gap-3.5 grid-cols-[repeat(auto-fill,minmax(min(100%,250px),1fr))]">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-[150px]" />
              ))}
            </div>
          ) : buckets.length === 0 ? (
            <div className="flex flex-wrap gap-5">
              <Card as="section" aria-labelledby="first-bucket" className="flex-[1_1_360px] p-6 flex flex-col gap-4">
                <span className="ps-icb">
                  <DriveIc className="w-5 h-5" />
                </span>
                <h2 id="first-bucket" className="m-0 text-lg font-medium">
                  Create your first bucket
                </h2>
                <form
                  className="flex flex-col gap-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const pid = quickProject || defaultCreateProject;
                    if (!pid) return;
                    // Plan choice and pricing are confirmed in the full dialog.
                    setCreateFor({ projectId: pid, name: quickName.trim(), isPublic: quickPublic });
                  }}
                >
                  <div>
                    <label htmlFor="quick-bucket" className="ps-label">
                      Bucket name
                    </label>
                    <input
                      id="quick-bucket"
                      className="ps-input font-mono"
                      placeholder="datasets-demo"
                      value={quickName}
                      onChange={(e) => setQuickName(e.target.value)}
                    />
                  </div>
                  <div>
                    <label htmlFor="quick-project" className="ps-label">
                      Project
                    </label>
                    <select
                      id="quick-project"
                      className="ps-input"
                      value={quickProject || defaultCreateProject}
                      onChange={(e) => setQuickProject(e.target.value)}
                    >
                      {projects.map((p: any) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <label className="flex items-center gap-2.5 text-sm text-ps-muted min-h-[44px]">
                    <input
                      type="checkbox"
                      checked={quickPublic}
                      onChange={(e) => setQuickPublic(e.target.checked)}
                      className="h-4 w-4 rounded border-ps-strong bg-ps-chrome text-ps-accent focus:ring-ps-accent"
                    />
                    Allow public read (for sharing files by URL)
                  </label>
                  <Button type="submit" className="self-start" disabled={!defaultCreateProject}>
                    Create bucket
                  </Button>
                </form>
              </Card>
              <S3Card snippet={s3Snippet} />
            </div>
          ) : (
            <>
              <div className="grid gap-3.5 grid-cols-[repeat(auto-fill,minmax(min(100%,250px),1fr))]">
                {buckets.map((bucket) => (
                  <BucketCard
                    key={`${bucket.project_id}:${bucket.id}`}
                    bucket={bucket}
                    projectName={scope === ALL ? projectName(bucket.project_id) : undefined}
                    onOpen={(tab) => setOpenBucket({ id: bucket.id, tab })}
                    onDelete={() => setDeletingBucketId(bucket.id)}
                  />
                ))}
              </div>
              <S3Card
                snippet={s3Snippet}
                onKeys={() => firstBucket && setOpenBucket({ id: firstBucket.id, tab: 'access' })}
              />
            </>
          )}
        </>
      )}

      {createFor && (
        <CreateBucketModal
          projectId={createFor.projectId}
          initialName={createFor.name}
          initialPublic={createFor.isPublic}
          onCreate={(data) => createMutation.mutate({ projectId: createFor.projectId, data })}
          onClose={() => setCreateFor(null)}
          isCreating={createMutation.isPending}
        />
      )}

      {openBucket &&
        (() => {
          const b = buckets.find((x) => x.id === openBucket.id);
          if (!b) return null;
          return (
            <BucketModal
              bucket={b}
              projectId={b.project_id}
              initialTab={openBucket.tab}
              onClose={() => setOpenBucket(null)}
              onUpdated={() => queryClient.invalidateQueries({ queryKey: ['buckets', b.project_id] })}
            />
          );
        })()}

      {deletingBucket &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4">
            <Card className="p-6 w-full max-w-sm flex flex-col gap-4" role="alertdialog" aria-labelledby="del-bucket-title">
              <div>
                <h3 id="del-bucket-title" className="m-0 text-base font-medium">
                  Delete bucket?
                </h3>
                <p className="m-0 text-sm text-ps-muted mt-1">
                  <strong className="text-ps-text">{deletingBucket.name}</strong> and all its objects will be permanently
                  removed.
                </p>
              </div>
              <div className="flex gap-3">
                <Button variant="secondary" className="flex-1" onClick={() => setDeletingBucketId(null)}>
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  className="flex-1"
                  onClick={() => deleteMutation.mutate(deletingBucket)}
                  disabled={deleteMutation.isPending}
                >
                  {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
                </Button>
              </div>
            </Card>
          </div>,
          document.body,
        )}
    </Page>
  );
}

function S3Card({ snippet, onKeys }: { snippet: string; onKeys?: () => void }) {
  return (
    <Card as="section" aria-labelledby="s3-tools" className="flex-[1_1_360px] min-w-0 p-6 flex flex-col gap-3">
      <h2 id="s3-tools" className="m-0 text-lg font-medium">
        Use any S3 tool
      </h2>
      <p className="m-0 text-sm text-ps-muted leading-relaxed">
        Point the AWS CLI, boto3 or rclone at your Podstack endpoint with access keys from a bucket’s Access tab. The
        endpoint is shown when you create keys.
      </p>
      <pre className="m-0 p-3.5 rounded-lg bg-ps-chrome border border-ps-border text-[13px] text-ps-text overflow-x-auto">
        {snippet}
      </pre>
      {onKeys ? (
        <Button variant="secondary" className="self-start" onClick={onKeys}>
          Create access keys
        </Button>
      ) : (
        <span className="text-xs text-ps-subtle">Create a bucket first, then generate keys for it.</span>
      )}
    </Card>
  );
}
